import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, Truck, XCircle, ChevronRight, ArrowLeft, Calendar, Search, CreditCard, RotateCcw, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import TokenExpiredError from '../components/TokenExpiredError';
import CustomerSidebar from '../features/customer/components/CustomerSidebar';
import CustomerBottomNavbar from '../features/customer/components/CustomerBottomNavbar';
import { orderSessionService } from '../services/api/orderSessionService';
import { publicOrderService } from '../services/api/publicOrderService';
import Badge from '../components/common/Badge';
import Card from '../components/common/Card';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import IconButton from '../components/common/IconButton';

const formatOrderDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
    const timePart = date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
    return `${datePart} • ${timePart}`;
};

const CustomerOrdersHistoryPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const rawToken = searchParams.get('token');
    const token = (rawToken && rawToken !== 'null' && rawToken !== 'undefined') ? rawToken : null;
    const activeToken = token || localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('All');
    const [customerData, setCustomerData] = useState(null);
    const [supplierData, setSupplierData] = useState(null);

    // Infinite scroll states
    const [page, setPage] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 15;

    useEffect(() => {
        const loadOrderHistory = async () => {
            let activeToken = token;
            if (!activeToken || activeToken === 'null' || activeToken === 'undefined') {
                activeToken = localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token');
            }

            if (!activeToken || activeToken === 'null' || activeToken === 'undefined') {
                console.log('[CustomerOrdersHistoryPage] No token found, redirecting to login...');
                navigate('/order/login');
                return;
            }

            try {
                // 1. Get Session Context
                const session = await orderSessionService.getSessionContext(activeToken);
                if (!session.valid) {
                    setError(session.errorMessage || 'Session expired');
                    setLoading(false);
                    return;
                }

                setCustomerData(session.customer);
                setSupplierData(session.tenant);

                // 2. Fetch Customer Orders (page 0)
                const response = await publicOrderService.getCustomerOrders(
                    session.customer.publicId,
                    session.tenant.supplierId,
                    0,
                    PAGE_SIZE,
                    true
                );

                const rawOrders = Array.isArray(response) ? response : (response.content || []);
                // Filter to show only history (terminal) orders
                const historyOrders = rawOrders.filter(order => {
                    const statusUpper = order.status?.toUpperCase();
                    return statusUpper === 'DELIVERED' || 
                           statusUpper === 'CANCELLED' || 
                           statusUpper === 'RETURNED';
                });
                const sortedOrders = historyOrders.sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.orderDate || 0);
                    const dateB = new Date(b.createdAt || b.orderDate || 0);
                    return dateB - dateA;
                });
                setOrders(sortedOrders);
                setFilteredOrders(sortedOrders);
                setPage(0);
                setHasMore(rawOrders.length === PAGE_SIZE);
                setLoading(false);

                // Clean token from URL query parameters so back-button doesn't auto-relogin
                if (token) {
                    if (token !== 'null' && token !== 'undefined') {
                        localStorage.setItem('customer_token', token);
                    }
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('token');
                    setSearchParams(newParams, { replace: true });
                }
            } catch (err) {
                console.error('[CustomerOrdersHistoryPage] Failed to load history:', err);
                setError('Failed to load order history. Please try again.');
                setLoading(false);
            }
        };

        loadOrderHistory();
    }, [token, searchParams, setSearchParams]);

    const loadNextPage = async () => {
        if (loadingMore || !hasMore || !customerData || !supplierData) return;

        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const response = await publicOrderService.getCustomerOrders(
                customerData.publicId,
                supplierData.supplierId,
                nextPage,
                PAGE_SIZE,
                true
            );

            const rawOrders = Array.isArray(response) ? response : (response.content || []);
            const historyOrders = rawOrders.filter(order => {
                const statusUpper = order.status?.toUpperCase();
                return statusUpper === 'DELIVERED' || 
                       statusUpper === 'CANCELLED' || 
                       statusUpper === 'RETURNED';
            });
            if (rawOrders.length === 0) {
                setHasMore(false);
            } else {
                setOrders(prev => {
                    const combined = [...prev, ...historyOrders];
                    return combined.sort((a, b) => {
                        const dateA = new Date(a.createdAt || a.orderDate || 0);
                        const dateB = new Date(b.createdAt || b.orderDate || 0);
                        return dateB - dateA;
                    });
                });
                setPage(nextPage);
                if (rawOrders.length < PAGE_SIZE) {
                    setHasMore(false);
                }
            }
        } catch (err) {
            console.error('[CustomerOrdersHistoryPage] Failed to load more orders:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            loadNextPage();
        }
    };

    // Handle filtering by search query, status, and date range
    useEffect(() => {
        const filtered = orders.filter(order => {
            // 1. Search Query Filter
            const query = searchQuery.trim().toLowerCase();
            let matchesQuery = true;
            if (query) {
                const matchesRef = order.orderRefNo?.toLowerCase().includes(query) || 
                                   order.publicId?.toLowerCase().includes(query);
                const matchesStatusName = order.status?.toLowerCase().includes(query);
                matchesQuery = matchesRef || matchesStatusName;
            }

            // 2. Status Filter
            let matchesStatus = true;
            if (statusFilter !== 'All') {
                const orderStatusUpper = order.status?.toUpperCase() || '';
                matchesStatus = orderStatusUpper === statusFilter;
            }

            // 3. Date Filter
            let matchesDate = true;
            if (dateFilter !== 'All') {
                const orderTime = new Date(order.createdAt || order.orderDate || 0).getTime();
                const nowTime = new Date().getTime();
                const diffDays = (nowTime - orderTime) / (1000 * 60 * 60 * 24);

                if (dateFilter === 'Today') {
                    const todayStr = new Date().toDateString();
                    const orderDayStr = new Date(order.createdAt || order.orderDate || 0).toDateString();
                    matchesDate = todayStr === orderDayStr;
                } else if (dateFilter === '7days') {
                    matchesDate = diffDays <= 7;
                } else if (dateFilter === '30days') {
                    matchesDate = diffDays <= 30;
                } else if (dateFilter === '90days') {
                    matchesDate = diffDays <= 90;
                }
            }

            return matchesQuery && matchesStatus && matchesDate;
        });
        setFilteredOrders(filtered);
    }, [searchQuery, statusFilter, dateFilter, orders]);

    const getBadgeVariant = (status) => {
        switch (status?.toUpperCase()) {
            case 'CREATED': case 'NEW': return 'info';
            case 'ACKNOWLEDGED': return 'primary';
            case 'PENDING_WAREHOUSE': case 'PROCESSING': return 'warning';
            case 'DESPATCHED': return 'primary';
            case 'DELIVERED': case 'READY': return 'success';
            case 'CANCELLED': return 'danger';
            default: return 'neutral';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'CREATED': case 'NEW': return <Package className="w-3.5 h-3.5" />;
            case 'ACKNOWLEDGED': return <CheckCircle className="w-3.5 h-3.5" />;
            case 'PENDING_WAREHOUSE': case 'PROCESSING': return <Clock className="w-3.5 h-3.5" />;
            case 'DESPATCHED': return <Truck className="w-3.5 h-3.5" />;
            case 'DELIVERED': case 'READY': return <CheckCircle className="w-3.5 h-3.5" />;
            case 'CANCELLED': return <XCircle className="w-3.5 h-3.5" />;
            default: return <Package className="w-3.5 h-3.5" />;
        }
    };

    const handleReorder = (order) => {
        if (!order || !order.items || !Array.isArray(order.items)) return;
        
        const cartItems = order.items.map(item => ({
            id: item.productPublicId || item.productId,
            name: item.productName || item.name,
            price: item.unitPrice || item.price || 0,
            quantity: item.quantity || 1,
            unit: item.unit || 'pcs',
            originalPrice: item.unitPrice || item.price || 0,
            hasDiscount: false
        }));

        localStorage.setItem('yaseero_reorder_items', JSON.stringify(cartItems));
        navigate(`/shop?token=${activeToken}&openCart=true`);
    };

    if (loading) return <LoadingSpinner message="Retrieving your order history..." />;
    if (error) return <TokenExpiredError message={error} />;

    return (
        <div className="flex h-[100dvh] w-screen bg-gray-50 dark:bg-slate-900 overflow-hidden font-sans">
            {/* Desktop Left Sidebar */}
            <CustomerSidebar 
                activeView="history"
                token={activeToken}
                customerData={customerData}
                supplierData={supplierData}
            />

            {/* Main Content Pane */}
            <div className="flex-1 flex flex-col min-w-0 h-[100dvh] relative pb-customer-nav lg:pb-0">
                {/* Header - Aligned with CustomerHeader */}
                <header className="bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700/50 px-4 py-1.5 md:px-6 md:py-2 flex-none z-30">
                    <div className="w-full flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <img
                                    src="/logo.jpg"
                                    alt="Logo"
                                    className="w-10 h-10 rounded-lg object-contain"
                                />
                                <img
                                    src="/name.jpg"
                                    alt="Yaseero"
                                    className="h-8 object-contain"
                                />
                            </div>
                            <span className="text-xs font-black text-cyan-600 uppercase tracking-widest bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-100 dark:border-cyan-900/30 px-3 py-1.5 rounded-xl">
                                Order History
                            </span>
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg px-3 py-1.5 mb-2">
                            <h2 className="font-bold text-gray-800 dark:text-gray-200 text-base">
                                {supplierData?.businessName || 'Supplier'}
                            </h2>
                        </div>

                        {/* Search Bar */}
                        <div className="relative mb-2">
                            <Input
                                placeholder="Search by Order # or Status..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                icon={Search}
                                rightElement={
                                    searchQuery ? (
                                        <IconButton
                                            icon={X}
                                            ariaLabel="Clear search"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setSearchQuery('')}
                                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                        />
                                    ) : null
                                }
                                containerClassName="w-full"
                                className="bg-gray-100 border-none rounded-xl dark:bg-slate-700 dark:text-slate-100"
                            />
                        </div>
                    </div>
                </header>

                {/* Scrollable Container */}
                <div className="flex-1 overflow-hidden flex flex-col transition-all duration-200 p-4 md:p-6 bg-slate-50/50 dark:bg-slate-900/50">
                    <div 
                        onScroll={handleScroll}
                        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
                    >
                        <div className="w-full max-w-4xl mx-auto pb-28 flex flex-col gap-6">
                            {/* Filters Bar */}
                            <div className="flex flex-wrap gap-3 mb-2 items-center justify-between px-1">
                                <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    {filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'} found in history
                                </span>
                                
                                <div className="flex gap-2 w-full sm:w-auto items-center">
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-bold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm cursor-pointer"
                                    >
                                        <option value="All">All Statuses</option>
                                        <option value="DELIVERED">Delivered</option>
                                        <option value="CANCELLED">Cancelled</option>
                                        <option value="RETURNED">Returned</option>
                                    </select>
                                    
                                    <select
                                        value={dateFilter}
                                        onChange={(e) => setDateFilter(e.target.value)}
                                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-bold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm cursor-pointer"
                                    >
                                        <option value="All">All Time</option>
                                        <option value="Today">Today</option>
                                        <option value="7days">Last 7 Days</option>
                                        <option value="30days">Last 30 Days</option>
                                        <option value="90days">Last 90 Days</option>
                                    </select>

                                    {(statusFilter !== 'All' || dateFilter !== 'All' || searchQuery !== '') && (
                                        <button
                                            onClick={() => {
                                                setSearchQuery('');
                                                setStatusFilter('All');
                                                setDateFilter('All');
                                            }}
                                            className="px-3 py-1.5 rounded-xl text-xs font-bold bg-rose-50 text-rose-600 border border-rose-100 dark:bg-rose-950/20 dark:text-rose-400 dark:border-rose-900/30 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors shadow-sm cursor-pointer whitespace-nowrap"
                                        >
                                            Reset
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Orders Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {filteredOrders.length === 0 ? (
                                    <Card className="col-span-full text-center py-16 border border-slate-100 dark:border-slate-800">
                                        <div className="bg-slate-50 dark:bg-slate-700 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100 dark:border-slate-600">
                                            <Package className="w-8 h-8 text-slate-400" />
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-1">No Orders Found</h3>
                                        <p className="text-slate-400 dark:text-slate-500 text-sm">We couldn't find any orders matching your filters.</p>
                                    </Card>
                                ) : (
                                    filteredOrders.map(order => (
                                        <Card
                                            key={order.publicId}
                                            onClick={() => navigate(`/status/${order.publicId}?token=${activeToken}&from=history`)}
                                            className="w-full p-5 flex flex-col justify-between gap-4 min-h-[160px]"
                                        >
                                            <div className="w-full flex justify-between items-start">
                                                <div>
                                                    <div className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-1">
                                                        <Calendar className="w-3.5 h-3.5" />
                                                        <span className="text-[11px] font-bold uppercase tracking-wider">
                                                            {formatOrderDate(order.createdAt || order.orderDate)}
                                                        </span>
                                                    </div>
                                                    <p className="font-black text-slate-900 dark:text-slate-100 text-2xl md:text-3xl mt-1">
                                                        #{order.orderRefNo || order.publicId.substring(0, 8)}
                                                    </p>
                                                </div>
                                                <Badge variant={getBadgeVariant(order.status)}>
                                                    <span className="flex items-center gap-1.5">
                                                        {getStatusIcon(order.status)}
                                                        {order.status}
                                                    </span>
                                                </Badge>
                                            </div>

                                            {/* Items preview inside the card */}
                                            {order.items && order.items.length > 0 && (
                                                <div className="w-full text-xs text-slate-500 space-y-2.5 py-3 px-1 border-t border-slate-50/80 dark:border-slate-700/50">
                                                    {order.items.slice(0, 3).map((item, idx) => (
                                                        <div key={idx} className="flex justify-between items-center gap-2">
                                                            <span className="truncate max-w-[220px] font-bold text-slate-800 dark:text-slate-200 text-sm">{item.productName || item.name}</span>
                                                            <span className="font-black text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-100/60 dark:border-cyan-900/40 px-2.5 py-1 rounded-xl text-xs whitespace-nowrap shadow-sm flex items-center gap-1.5">
                                                                <span>Qty: {item.quantity}</span>
                                                                {item.unit && (
                                                                    <span className="inline-block px-1.5 py-0.5 bg-cyan-100/60 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-300 rounded text-[9px] font-bold">
                                                                        {item.unit}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    {order.items.length > 3 && (
                                                        <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider pl-0.5 pt-1">
                                                            + {order.items.length - 3} more items
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="w-full flex items-center justify-between border-t border-slate-50 dark:border-slate-700/50 pt-4 mt-auto">
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">Total Amount</p>
                                                    <p className="font-black text-cyan-600 dark:text-cyan-400 text-xl">AED {order.totalAmount?.toFixed(2)}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleReorder(order);
                                                        }}
                                                        variant="secondary"
                                                        size="sm"
                                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-cyan-600 dark:text-cyan-400 border-cyan-100/50 dark:border-cyan-900/20 hover:bg-cyan-50 dark:hover:bg-cyan-950/20"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                        Reorder
                                                    </Button>
                                                    <div className="flex items-center gap-1 text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded-xl group-hover:text-cyan-600 transition-colors">
                                                        View Track <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    ))
                                )}
                            </div>

                            {loadingMore && (
                                <div className="py-4 flex justify-center items-center text-slate-500 text-xs gap-2">
                                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500"></span>
                                    <span>Loading more orders...</span>
                                </div>
                            )}

                            {/* Inline Page Footer */}
                            <footer className="py-6 text-center border-t border-slate-100/60 mt-auto">
                                <p className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase tracking-wider">
                                    Securely powered by <span className="text-slate-500 font-extrabold">Yaseero</span>
                                </p>
                            </footer>
                        </div>
                    </div>
                </div>

                {/* Mobile / Tablet Bottom Navigation */}
                <CustomerBottomNavbar 
                    activeView="history"
                    token={activeToken}
                    supplierData={supplierData}
                />
            </div>
        </div>
    );
};

export default CustomerOrdersHistoryPage;
